'use client'

import { usePathname } from 'next/navigation'
import { Redirect } from '../CMS/dynamic/Redirect'
import { FreeAgentDisplay } from './components/registration/FreeAgentDisplay'
import { TeamsDisplay } from './components/registration/TeamsDisplay'
import { TeamDisplay } from './components/team/_TeamDisplay'
import { TeamsAdminDisplay } from './components/teams/TeamsAdminDisplay'
import { RcIcon } from '@/components/atoms/RcIcon'
import { RcTrans } from '@/components/atoms/RcTrans'
import { PageItem } from '@/components/organisms/page/PageItem'
import {
  PageNavSwitch,
  PageNavSwitchOption
} from '@/components/organisms/page/PageNavSwitch'
import { useNavigation } from '@/core/hooks/useNavigation'
import {
  CompetitionRoute,
  CompetitionTeamsRoute,
  RootRoute
} from '@/core/route-keys'
import { useCompetition } from '@/entity/competition/useCompetition'
import { useCompetitionParticipant } from '@/entity/competition/useCompetitionParticipant'
import { first, some } from 'lodash-es'

export const CompetitionTeamRouter = () => {
  const {
    competition,
    isFreeAgentMode,
    isSoloCompetition,
    isCompetitionModerator
  } = useCompetition()
  const { involvement, isCoach, hasTeam } = useCompetitionParticipant()
  const { getPath } = useNavigation()
  const pathname = usePathname()

  const options: PageNavSwitchOption[] = []

  // multiple teams or coach view is a table
  if (involvement?.length! > 1 || isCoach()) {
    options.push({
      fill: true,
      component: () => <MyTeamsDisplay />,
      text: <RcTrans i18nKey='competition:registration.my-teams-navigation' />,
      icon: <RcIcon icon={['fal', 'user-headset']} />,
      to: CompetitionTeamsRoute.Mine
    })
  } else if (hasTeam()) {
    const entryId = first(involvement)?.entry?.id?.toString() || ''
    options.push({
      fill: true,
      active: pathname.includes(entryId),
      component: () => (
        <Redirect
          replace
          path={getPath({
            root: RootRoute.Competition,
            rootId: competition?.id,
            subRoute: CompetitionRoute.Team,
            subId: entryId
          })}
        />
      ),
      text: <RcTrans i18nKey='competition:registration.your-team-navigation' />,
      icon: <RcIcon icon={['fal', 'user-headset']} />,
      to: CompetitionTeamsRoute.Mine
    })
  }

  options.push({
    fill: true,
    component: () => <TeamsDisplay />,
    text: isCompetitionModerator ? (
      <RcTrans i18nKey='competition:registration.player-teams-navigation' />
    ) : (
      <RcTrans i18nKey='competition:registration.all-teams-navigation' />
    ),
    icon: <RcIcon icon={['fal', 'users']} />,
    to: CompetitionTeamsRoute.All
  })

  if (isFreeAgentMode && !isSoloCompetition) {
    options.push({
      component: () => <FreeAgentDisplay />,
      text: (
        <RcTrans i18nKey='competition:registration.free-agent-navigation' />
      ),
      icon: <RcIcon icon={['fal', 'user-bounty-hunter']} />,
      to: CompetitionTeamsRoute.FreeAgent
    })
  }

  return (
    <PageItem contained={false}>
      <PageNavSwitch options={options} extraRoutes={<TeamDisplay />} />
    </PageItem>
  )
}

const MyTeamsDisplay = () => {
  return <TeamsAdminDisplay selfOnly />
}
